import { Outlet, useNavigate } from "react-router-dom";
import AdminTopbar from "./AdminTopbar";
import AdminSidebar from "./AdminSidebar";
import { useState, useCallback, useEffect } from "react";
import { useAuth } from '@/contexts/auth';
import { AuthError } from "@/components/ui/auth-error";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // Handle authentication errors
  useEffect(() => {
    const handleUnauthorizedError = (event: MessageEvent) => {
      if (event.data?.type === 'AUTH_ERROR') {
        setError(event.data.message || 'Authentication error');
      }
    };
    
    window.addEventListener('message', handleUnauthorizedError);
    return () => {
      window.removeEventListener('message', handleUnauthorizedError);
    };
  }, []);
  
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [sidebarOpen]);
  
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);
  
  const handleLogout = useCallback(() => {
    logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden">
      {/* Topbar - fixed height */}
      <div className="relative z-50 flex-none">
        <AdminTopbar onMenuClick={toggleSidebar} onLogout={handleLogout} />
      </div>
      
      {/* Display authentication errors */}
      {error && (
        <div className="px-4 py-2">
          <AuthError message={error} />
        </div>
      )}
      
      {/* Content area - takes remaining height */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile sidebar overlay */}
        <div 
          className={`fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
        
        {/* Sidebar - fixed width, full height */}
        <div 
          className={`fixed inset-y-0 left-0 z-45 w-64 transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          style={{ backgroundColor: 'var(--background)' }}
        >
          <div className="absolute right-0 top-0 -mr-12 pt-2 lg:hidden">
            <Button 
              variant="ghost" 
              size="icon" 
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-white" 
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close sidebar</span>
            </Button>
          </div>
          <div className="h-full overflow-y-auto">
            <AdminSidebar onLinkClick={closeSidebar} />
          </div>
        </div>
        
        {/* Main content - scrollable container */}
        <div className="flex-1 w-full relative overflow-hidden">
          <main className="absolute inset-0 overflow-y-auto">
            <div className="p-4 sm:p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
